import { Controller } from "@hotwired/stimulus"
import { syncGateReason } from "../lib/gate_reason"
import { Modal } from "bootstrap"
import { get } from "@rails/request.js"

// Onboarding Step 1 — Agreements review gate.
// Tracks per-policy review state, conditionally enables the consent checkbox
// once all 3 are reviewed, and conditionally enables the submit button once
// the checkbox is checked. Fetches policy content from /welcome/* pages into
// a shared modal.
const REVIEW_DELAY_MS = 2000
const POLICY_CONTENT_SELECTOR = ".landing-policy__body"

export default class extends Controller {
  // The two gate reasons (A11Y-1) are authored ONCE, in
  // onboarding/steps/_agreements.html.haml, which paints them server-side on
  // the disabled buttons and stamps the same strings here as values — so the
  // first paint and every later refresh say the same thing. The defaults only
  // serve a DOM that stamps nothing (the Jest fixtures).
  static values = {
    dwellReason: { type: String, default: "Take a moment to read the policy first." },
    submitReason: { type: String, default: "Please review all policies and check the agreement box to continue." }
  }

  static targets = [
    "form", "policyRow", "policyStatus", "consentLabel", "agreeCheckbox", "consentHint",
    "modal", "modalTitle", "modalBody"
  ]

  connect() {
    this.reviewed = new Set()
    if (this.hasModalTarget) {
      this._modal = Modal.getOrCreateInstance(this.modalTarget)
    } else {
      this._modal = null
      // Defense-in-depth: silent failure here means every Read-full click is
      // a no-op, leaving users stranded on step 1. Loud-fail in dev so a
      // future structural regression doesn't ship to prod.
      console.warn("[onboarding-agreements] modal target missing — policy review will silently fail. Ensure .modal#onboarding-policy-modal is nested inside the data-controller element.")
    }
    this._openedAt = null
    this._activePolicy = null
    this._contentCache = new Map()

    if (this.hasModalTarget) {
      this._dwellTimer = null
      this._onShown = () => {
        this._openedAt = Date.now()
        // Keep "I've reviewed this" disabled until the dwell window elapses.
        // Otherwise an early click dismisses the modal before dwell passes,
        // leaving the policy unreviewed with no visual feedback.
        this._setDoneButtonEnabled(false)
        this._dwellTimer = setTimeout(() => this._setDoneButtonEnabled(true), REVIEW_DELAY_MS)
      }
      this._onHidden = () => {
        if (this._dwellTimer) { clearTimeout(this._dwellTimer); this._dwellTimer = null }
        this.handleModalClose()
      }
      this.modalTarget.addEventListener("shown.bs.modal", this._onShown)
      this.modalTarget.addEventListener("hidden.bs.modal", this._onHidden)
    }

    this.refreshConsentEnabled()
    this.refreshSubmit()
  }

  disconnect() {
    if (this.hasModalTarget) {
      this.modalTarget.removeEventListener("shown.bs.modal", this._onShown)
      this.modalTarget.removeEventListener("hidden.bs.modal", this._onHidden)
    }
    if (this._dwellTimer) { clearTimeout(this._dwellTimer); this._dwellTimer = null }
  }

  _setDoneButtonEnabled(enabled) {
    if (!this.hasModalTarget) return
    const btn = this.modalTarget.querySelector(".gl-onboarding-modal-done")
    if (!btn) return
    syncGateReason(btn, { ready: enabled, reason: enabled ? "" : this.dwellReasonValue })
  }

  // ── Modal open + content fetch ───────────────────────────────────────
  openPolicy(event) {
    event.preventDefault()
    const btn = event.currentTarget
    const policyKey = btn.dataset.policy
    const url = btn.dataset.url
    const title = btn.dataset.title

    this._activePolicy = policyKey
    if (this.hasModalTitleTarget) this.modalTitleTarget.textContent = title

    if (this._contentCache.has(policyKey)) {
      this.renderModalBody(this._contentCache.get(policyKey))
    } else {
      this.showLoadingState()
      this.fetchPolicyContent(url).then(html => {
        this._contentCache.set(policyKey, html)
        if (this._activePolicy === policyKey) this.renderModalBody(html)
      }).catch(err => {
        this.renderModalBody(
          `<div class="gl-alert gl-alert--warning" role="alert">` +
          `<i class="ph ph-warning-circle gl-alert-icon"></i>` +
          `<div class="gl-alert-content">Could not load policy. ` +
          `<a href="${url}" target="_blank" rel="noopener noreferrer">Open in new tab</a>.</div>` +
          `</div>`
        )
        console.error("[onboarding-agreements] policy fetch failed", err)
      })
    }

    if (this._modal) this._modal.show()
  }

  showLoadingState() {
    if (!this.hasModalBodyTarget) return
    this.modalBodyTarget.innerHTML =
      `<div class="gl-modal-loading" role="status">
         <div class="gl-modal-loading-spinner" aria-hidden="true"></div>
         <p class="gl-modal-loading-text">Loading policy…</p>
       </div>`
  }

  async fetchPolicyContent(url) {
    // @rails/request.js handles CSRF + headers automatically; no manual
    // Accept header needed (responseKind: "html" sets it).
    const response = await get(url, { responseKind: "html" })
    if (!response.ok) throw new Error(`HTTP ${response.statusCode}`)
    const html = await response.text
    const doc = new DOMParser().parseFromString(html, "text/html")
    const body = doc.querySelector(POLICY_CONTENT_SELECTOR)
    return body ? body.outerHTML : doc.body.innerHTML
  }

  renderModalBody(html) {
    if (this.hasModalBodyTarget) this.modalBodyTarget.innerHTML = html
  }

  handleModalClose() {
    if (!this._activePolicy) return
    const dwell = Date.now() - (this._openedAt || 0)
    if (dwell >= REVIEW_DELAY_MS) this.markReviewed(this._activePolicy)
    this._activePolicy = null
    this._openedAt = null
  }

  // ── Review state ─────────────────────────────────────────────────────
  markReviewed(policyKey) {
    if (this.reviewed.has(policyKey)) return
    this.reviewed.add(policyKey)
    this.updateRowStatus(policyKey)
    this.refreshConsentEnabled()
  }

  updateRowStatus(policyKey) {
    const status = this.policyStatusTargets.find(el => el.dataset.policy === policyKey)
    if (!status) return
    const pending = status.querySelector(".gl-onboarding-agreement-status-pending")
    const done    = status.querySelector(".gl-onboarding-agreement-status-done")
    if (pending) pending.hidden = true
    if (done) done.hidden = false
    const row = this.policyRowTargets.find(el => el.dataset.policy === policyKey)
    if (row) row.classList.add("gl-onboarding-agreement--reviewed")
  }

  // ── Gate transitions ─────────────────────────────────────────────────
  refreshConsentEnabled() {
    if (!this.hasAgreeCheckboxTarget) return
    const totalPolicies = this.policyRowTargets.length
    // `totalPolicies > 0` guards a DOM with no policyRow targets — without it,
    // an empty DOM satisfies `0 >= 0` and enables the consent checkbox immediately.
    const allReviewed = totalPolicies > 0 && this.reviewed.size >= totalPolicies
    this.agreeCheckboxTarget.disabled = !allReviewed
    this.syncConsentHint(allReviewed)
    if (this.hasConsentLabelTarget) {
      this.consentLabelTarget.classList.toggle("gl-onboarding-consent--enabled", allReviewed)
    }
    if (!allReviewed) {
      this.agreeCheckboxTarget.checked = false
      this.refreshSubmit()
    }
  }

  // A11Y-1 (#13369): the locked consent box says why in VISIBLE helper copy —
  // the consentHint target, whose text _agreements authors once — never a
  // gl-tooltip: a checkbox is a replaced element that renders no ::after
  // bubble, and a disabled one takes no keyboard focus. While any policy is
  // unread the hint shows and describes the box (aria-describedby); the moment
  // the last one is read both leave, so the page never asks for something the
  // user already did. Any other description on the box is kept.
  syncConsentHint(unlocked) {
    if (!this.hasConsentHintTarget) return
    const hint = this.consentHintTarget
    const box = this.agreeCheckboxTarget
    hint.hidden = unlocked
    const others = (box.getAttribute("aria-describedby") || "").split(/\s+/).filter((id) => id && id !== hint.id)
    const ids = unlocked ? others : [...others, hint.id].filter(Boolean)
    if (ids.length > 0) {
      box.setAttribute("aria-describedby", ids.join(" "))
    } else {
      box.removeAttribute("aria-describedby")
    }
  }

  refreshSubmit() {
    const submit = this.formSubmitButton()
    if (!submit) return
    const enabled = this.hasAgreeCheckboxTarget && this.agreeCheckboxTarget.checked
    syncGateReason(submit, { ready: enabled, reason: enabled ? "" : this.submitReasonValue })
  }

  formSubmitButton() {
    if (!this.hasFormTarget) return null
    return this.formTarget.querySelector('button[type="submit"], input[type="submit"]')
  }
}
